import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bell, MessageCircle, CreditCard, Truck, PackageCheck, ShoppingBag, ClipboardCheck, ClipboardList, Settings } from 'lucide-react';
import api from '@/lib/api';

// Phase 15-B · Seller notification (Kakao Alimtalk) preference editor.
// Real sends are Mocked in this phase — labels below make the pricing intent
// explicit so sellers know each toggle will incur wallet cost once the real
// provider is live (Phase 15-C).

// KAKAO V1 · 고객 알림톡 설정 화면 정리.
// 유지: 자동으로 발생하는 공통 이벤트 (판매자가 건별 선택 불가한 것들).
// 제거: inquiry_answered (CS 답변에서 건별 체크박스로 이동), delivery_completed
//       (LM_DELIVERY_COMPLETED 미승인 · EXTERNAL APPROVAL REQUIRED),
//       broadcast_alert (방송 관리에서 방송별 관리), payment_reminder
//       (주문관리 → 미결제 → 판매자 수동 발송).
const EVENTS = [
  { key: 'order_created',           label: '주문 접수',       desc: '주문이 접수되면 구매자에게 알림톡 발송', Icon: ShoppingBag },
  { key: 'comment_order_confirmed', label: '댓글주문 확정',   desc: '판매자가 댓글주문을 확정해 결제 링크가 생성되면 발송', Icon: ClipboardCheck },
  { key: 'payment_completed',       label: '결제 완료',       desc: '결제가 완료되면 구매자에게 알림톡 발송', Icon: CreditCard },
  { key: 'shipping_started',        label: '배송 시작',       desc: '송장이 등록되고 배송이 시작되면 알림톡 발송', Icon: Truck },
];

export default function SellerNotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [unitPrice, setUnitPrice] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, prices] = await Promise.all([
        api.get('/seller/notifications/settings'),
        api.get('/seller/service-prices').catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      const kk = (prices.data || []).find((p) => p.service_type === 'kakao_alimtalk');
      if (kk) setUnitPrice(kk.unit_price);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '설정 불러오기 실패');
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/seller/notifications/settings', { [key]: !settings[key] });
      setSettings(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-8 text-center text-xs text-gray-500">불러오는 중…</div>;

  const master = !!settings.kakao_enabled;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto p-4 pb-16" data-testid="seller-notif-settings">
        <Link to="/seller/dashboard" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black dark:hover:text-white pt-2 pb-3" data-testid="notif-back">
          <ArrowLeft size={14} /> 판매자 대시보드
        </Link>

        {/* Sub-nav (settings vs logs) */}
        <div className="flex items-center gap-1 mb-3 border-b border-black/10 dark:border-white/10 -mx-4 px-4">
          <Link to="/seller/notifications" data-testid="notif-subnav-settings"
            className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold border-b-2 border-[#FF3B30] text-[#FF3B30] -mb-px">
            <Settings size={13} /> 고객 알림톡 설정
          </Link>
          <Link to="/seller/notifications/logs" data-testid="notif-subnav-logs"
            className="inline-flex items-center gap-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold border-b-2 border-transparent text-gray-500 hover:text-black dark:hover:text-white -mb-px">
            <ClipboardList size={13} /> 알림 발송내역
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <Bell size={16} className="text-[#FF3B30]" />
          <h1 className="text-xl font-black">고객 알림톡 설정</h1>
          <span className="text-[10px] font-black bg-orange-500 text-white px-1.5 py-0.5 rounded-full">TEST MODE</span>
        </div>
        <p className="text-[11px] text-gray-500 mb-4 leading-relaxed">
          카카오 알림톡은 <b>선택형 유료 기능</b>입니다. 여기서 켠 이벤트만 구매자에게 알림톡이 발송되고, 발송 시 판매자 충전금에서 건당 요금이 차감됩니다.
          LIVEMOA <b>앱 내부 알림</b>은 이 설정과 무관하게 항상 무료로 유지됩니다.
        </p>

        {/* Master switch */}
        <div className={`rounded-2xl border p-4 mb-3 ${master ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-black/10 dark:border-white/10'}`} data-testid="notif-master-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-black inline-flex items-center gap-1"><Bell size={13} /> 카카오 알림톡 사용</p>
              <p className="text-[11px] text-gray-500 mt-1">
                이 스위치가 OFF이면 아래 개별 이벤트가 ON이어도 카카오 알림톡은 전혀 발송되지 않습니다. 앱 내부 알림은 정상 유지.
              </p>
              {unitPrice != null && (
                <p className="text-[10px] text-gray-400 mt-1">현재 알림톡 단가: <b>{unitPrice.toLocaleString('ko-KR')}원 / 건</b> · 실제 차감은 정식 오픈 후 시작</p>
              )}
            </div>
            <button
              onClick={() => toggle('kakao_enabled')}
              disabled={saving}
              data-testid="notif-master-toggle"
              className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${master ? 'bg-[#FF3B30]' : 'bg-black/20 dark:bg-white/20'}`}
              aria-label="master toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${master ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Per-event toggles */}
        <div className="grid gap-2">
          {EVENTS.map(({ key, label, desc, Icon }) => {
            const on = !!settings[key];
            return (
              <div key={key}
                className={`rounded-2xl border p-3 flex items-start justify-between gap-3 ${master ? '' : 'opacity-60'}`}
                data-testid={`notif-event-${key}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black inline-flex items-center gap-1.5">
                    <Icon size={12} className="text-[#FF3B30]" /> {label}
                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${on && master ? 'bg-emerald-500 text-white' : 'bg-black/10 dark:bg-white/10 text-gray-500'}`}>
                      {on && master ? 'ON' : 'OFF'}
                    </span>
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{desc}</p>
                  {unitPrice != null && on && master && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                      발송 시 <b>{unitPrice.toLocaleString('ko-KR')}원 / 건</b> 충전금 차감 (정식 오픈 후)
                    </p>
                  )}
                </div>
                <button
                  onClick={() => toggle(key)}
                  disabled={saving || !master}
                  data-testid={`notif-toggle-${key}`}
                  className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-[#FF3B30]' : 'bg-black/20 dark:bg-white/20'} ${!master ? 'cursor-not-allowed' : ''}`}
                  aria-label={`${label} toggle`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-gray-400 mt-4 leading-relaxed">
          ⓘ 정식 오픈 전(TEST MODE) 에는 실제 카카오톡이 발송되지 않고 시스템 로그에만 기록됩니다.
          카카오 비즈니스 채널 인증과 알림톡 템플릿 심사가 완료되면 자동으로 실 발송으로 전환됩니다.
        </p>
      </div>
    </div>
  );
}
